import { PageOptions } from '@graphcommerce/framer-next-pages'
import { CountryRegionsDocument, StoreConfigDocument } from '@graphcommerce/magento-store'
import { GetStaticProps, LayoutOverlay, LayoutOverlayHeader, LayoutOverlayProps, LayoutTitle, MetaRobots, PageMeta } from '@graphcommerce/next-ui'
import { RowRenderer } from '../../components'
import { LayoutDocument } from '../../components/Layout/Layout.gql'
import { DefaultPageDocument, DefaultPageQuery } from '../../graphql/DefaultPage.gql'
import { graphqlSharedClient, graphqlSsrClient } from '../../lib/graphql/graphqlSsrClient'
import { ApolloClient, InMemoryCache, gql } from '@graphcommerce/graphql'
import { useEffect, useState } from 'react'
import { Trans } from '@lingui/react'
import { Box, Container } from '@mui/material'
import EstimateForm from 'components/ChipLabCustom/Shipping/EstimateForm'
import { ProductListWithReviewsDocument } from 'graphql/ProductListWithReviews.gql'

type Props = DefaultPageQuery & {
  tokenData?: any
  products?: any
  countries?: any
}
type RouteProps = { url: string }
type GetPageStaticProps = GetStaticProps<LayoutOverlayProps, Props, RouteProps>

function ShippingInfoPage(props: Props) {
  const { pages, tokenData, products, countries } = props
  const page = pages?.[0]

  const [shippingData, setShippingData] = useState();
  const [items, setItems] = useState([]) as any;
  const [country, setCountry] = useState(countries?.find((o) => o?.two_letter_abbreviation === 'US'));
  const [region, setRegion] = useState('');
  const [zipCode, setZipCode] = useState();
  const [submitted, setSubmitted] = useState(false);

  function submit() {
    setSubmitted(true);
  }

  useEffect(() => {
    if (submitted) {
      const shipperClient = new ApolloClient({
        uri: 'https://api.shipperhq.com/v2/graphql',
        headers: {
          'X-ShipperHQ-Secret-Token': tokenData?.createSecretToken?.token,
          'X-ShipperHQ-Scope': 'DEVELOPMENT'
        },
        cache: new InMemoryCache(),
      });

      const allItems: any = items.map((item) => {
        const obj = item?.obj;
        const price = obj?.price_tiers?.find((o) => o?.quantity == item?.quantity)
        return {
          itemId: obj?.uid,
          sku: obj?.sku,
          storePrice: price?.final_price?.value,
          weight: 0.02,
          qty: item?.quantity,
          type: "CONFIGURABLE",
          attributes: [
            {
              name: "shipperhq_shipping_group",
              value: "custom item"
            }
          ]
        };
      });

      const destination: any = {
        country: country?.two_letter_abbreviation,
        region: region,
        city: "",
        zipcode: zipCode
      };

      const data = shipperClient.query({
        query: gql`
          query retrieveShippingQuote($items: [ItemInput]!, $destination: DestinationInput!) {
            retrieveShippingQuote(ratingInfo: {
              cart: {
                items: $items
              },
              destination: $destination,
                siteDetails: {
                    appVersion: "1.0.0",
                    ecommerceCart: "Your Integrated Platform",
                    ecommerceVersion: "1.0.0",
                    websiteUrl: "www.chiplab.com",
                    ipAddress: "0.0.0.0"
                }
            }) {
              transactionId
              carriers {
                carrierCode
                carrierTitle
                carrierType
                error {
                  errorCode
                  internalErrorMessage
                  externalErrorMessage
                  priority
                }
                shippingRates {
                  code
                  title
                  totalCharges
                }
              }
              errors {
                errorCode
                internalErrorMessage
                externalErrorMessage
                priority
              }
            }
          }
          `
        ,
        variables: {
          destination: destination,
          items: allItems
        },
      });

      transferData(data);
      setSubmitted(false);
    }
  }, [submitted]);

  async function transferData(data) {
    const newData = (await data).data;

    setShippingData(newData);
  }

  return (
    <>
      <PageMeta
        title={page?.metaTitle ?? page?.title ?? ''}
        metaDescription={page?.metaDescription ?? ''}
        metaRobots={['noindex']}
      />
      <LayoutOverlayHeader>
        <LayoutTitle component='span' size='small'>
          <Trans id='Shipping Information' />
        </LayoutTitle>
      </LayoutOverlayHeader>

      <Container maxWidth='lg' >
        <Box
          sx={(theme) => ({
            [theme.breakpoints.up('md')]: {
              border: 1,
            },
            borderRadius: '8px',
            padding: 3,
            display: 'flex',
            flexDirection: 'column',
            marginTop: theme.spacings.lg,
            marginBottom: theme.spacings.lg,
          })}
        >
          <EstimateForm
            products={products?.items}
            items={items}
            setItems={setItems}
            country={country}
            setCountry={setCountry}
            region={region}
            setRegion={setRegion}
            setZipCode={setZipCode}
            countries={countries}
            submit={submit}
            shippingData={shippingData}
          />
        </Box>
      </Container>

      {page && (
        <RowRenderer
          content={page.content}
        />
      )}
    </>
  )
}

ShippingInfoPage.pageOptions = {
  Layout: LayoutOverlay,
  layoutProps: { variantMd: 'bottom', variantSm: 'bottom' },
  overlayGroup: 'shipping-info'
} as PageOptions

export default ShippingInfoPage

export const getStaticProps: GetPageStaticProps = async ({ locale }) => {
  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)
  const shipperClient = new ApolloClient({
    uri: 'https://rms.shipperhq.com',
    cache: new InMemoryCache(),
  });

  const conf = client.query({ query: StoreConfigDocument })

  const page = staticClient.query({ query: DefaultPageDocument, variables: { url: `page/shipping` } })
  const layout = staticClient.query({ query: LayoutDocument })

  // todo(paales): Remove when https://github.com/Urigo/graphql-mesh/issues/1257 is resolved

  const tokenData = shipperClient.mutate({
    mutation: gql`
      mutation CreateSecretToken($api_key: String!, $auth_code: String!) { 
        createSecretToken(api_key: $api_key, auth_code: $auth_code) {
          token  
        }
      }
    `,
    variables: { api_key: '6cfda586ffa86181c28860bcf605f1d1', auth_code: '317eadadabffd4c536c93ca5b134543e8cc23b5afab1145297' },
  })

  const productData = client.query({
    query: ProductListWithReviewsDocument,
    variables: { pageSize: 8, filters: { category_uid: { eq: 'ODE=' } }, categoryUid: 'ODE=' },
  })

  const countriesData = client.query({ query: CountryRegionsDocument });

  if (!(await page).data.pages?.[0]) return { notFound: true }

  return {
    props: {
      ...(await page).data,
      ...(await layout).data,
      ...(await productData).data,
      tokenData: (await tokenData).data,
      countries: (await countriesData).data.countries,
      apolloState: await conf.then(() => client.cache.extract()),
    },
    revalidate: 60 * 20,
  }
}
